import React, { useState, useEffect } from "react";
import { Plus, RefreshCw, X, Calendar, Clock, Check, XCircle } from "lucide-react";
import { FaBuilding, FaSwimmingPool, FaTableTennis, FaDumbbell, FaTheaterMasks, FaTree, FaCog } from "react-icons/fa";
import Modal from './Modal'
import { API_BASE } from '../config'
import { localDateString } from '../utils/dateUtils'

async function apiFetch(path, opts = {}) {
  const token = sessionStorage.getItem("id_token");
  const resp = await fetch(`${API_BASE}${path}`, {
    ...opts,
    headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}`, ...opts.headers },
  });
  if (!resp.ok) {
    const body = await resp.text().catch(() => "");
    throw new Error(body || `API ${resp.status}`);
  }
  return resp.json();
}

// Icon lookup by icon key stored in DB
const ICON_MAP = {
  clubhouse: FaBuilding,
  swimming_pool: FaSwimmingPool,
  gym: FaDumbbell,
  party_hall: FaTheaterMasks,
  indoor_games: FaTableTennis,
  garden: FaTree,
};

function getIcon(iconKey) {
  return ICON_MAP[iconKey] || FaCog;
}

const TIME_SLOTS = [
  { value: "06:00-08:00", label: "6:00 AM – 8:00 AM" },
  { value: "08:00-10:00", label: "8:00 AM – 10:00 AM" },
  { value: "10:00-12:00", label: "10:00 AM – 12:00 PM" },
  { value: "12:00-14:00", label: "12:00 PM – 2:00 PM" },
  { value: "14:00-16:00", label: "2:00 PM – 4:00 PM" },
  { value: "16:00-18:00", label: "4:00 PM – 6:00 PM" },
  { value: "18:00-20:00", label: "6:00 PM – 8:00 PM" },
  { value: "20:00-22:00", label: "8:00 PM – 10:00 PM" },
];

const STATUS_BADGES = {
  pending: "bg-yellow-100 text-yellow-800",
  approved: "bg-green-100 text-green-800",
  rejected: "bg-red-100 text-red-800",
  cancelled: "bg-gray-100 text-gray-600",
};

function formatDate(val) {
  if (!val) return "";
  try {
    const d = new Date(val);
    return d.toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" });
  } catch { return val; }
}

function getSlotLabel(val) {
  return TIME_SLOTS.find((s) => s.value === val)?.label || val;
}

function getAmenityInfo(val, amenities) {
  const found = amenities.find((a) => a.AMENITY_ID === val || a.ICON === val);
  if (found) return { label: found.NAME, icon: getIcon(found.ICON) };
  return { label: val, icon: FaBuilding };
}

export default function ClubhouseBooking({ trialMaxPerMonth, isTrial }) {
  const [bookings, setBookings] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [toasts, setToasts] = useState([]);
  const [filterStatus, setFilterStatus] = useState("all");
  const [amenities, setAmenities] = useState([]);
  const [amenitiesLoading, setAmenitiesLoading] = useState(true);

  const communityId = sessionStorage.getItem("community_id") || "";
  const unitNbr = sessionStorage.getItem("unit_nbr") || "";
  const userEmail = (() => {
    try {
      const t = sessionStorage.getItem("id_token");
      if (!t) return "";
      const payload = JSON.parse(atob(t.split(".")[1]));
      return payload.email || "";
    } catch { return ""; }
  })();
  const userName = (() => {
    try {
      const t = sessionStorage.getItem("id_token");
      if (!t) return "";
      const payload = JSON.parse(atob(t.split(".")[1]));
      return payload.name || payload.email?.split("@")[0] || "";
    } catch { return ""; }
  })();

  const today = localDateString();

  const [form, setForm] = useState({
    amenity: "",
    bookingDate: "",
    slot: "",
    purpose: "",
  });

  function showToast(message, type = "success") {
    const id = Date.now();
    setToasts((prev) => [...prev, { id, message, type }]);
    setTimeout(() => setToasts((prev) => prev.filter((t) => t.id !== id)), 3000);
  }

  async function loadBookings() {
    if (!communityId) return;
    setLoading(true);
    try {
      const data = await apiFetch(`/bookings?communityId=${communityId}&unitNbr=${unitNbr}`);
      const sorted = (data || []).sort((a, b) => (b.CREATED_AT || "").localeCompare(a.CREATED_AT || ""));
      setBookings(sorted);
    } catch (err) {
      console.error("loadBookings error:", err);
      showToast("Failed to load bookings", "error");
    } finally {
      setLoading(false);
    }
  }

  async function loadAmenities() {
    if (!communityId) return;
    setAmenitiesLoading(true);
    try {
      const data = await apiFetch(`/amenities?communityId=${communityId}`);
      const active = (data || []).filter((a) => a.STATUS !== "inactive");
      setAmenities(active);
    } catch (err) {
      console.error("loadAmenities error:", err);
    } finally {
      setAmenitiesLoading(false);
    }
  }

  useEffect(() => { loadBookings(); loadAmenities(); }, [communityId]);

  // Trial: count this calendar month's non-cancelled bookings by this resident
  const thisMonthBookingCount = (() => {
    const ym = new Date().toISOString().slice(0, 7) // "YYYY-MM"
    return bookings.filter(b => {
      const status = (b.STATUS || '').toLowerCase()
      return status !== 'cancelled' && (b.BOOKING_DATE || b.CREATED_AT || '').startsWith(ym)
    }).length
  })()
  const bookingAtLimit = trialMaxPerMonth != null && trialMaxPerMonth >= 0 && thisMonthBookingCount >= trialMaxPerMonth

  async function handleSubmit(e) {
    e.preventDefault();
    if (!form.amenity || !form.bookingDate || !form.slot) {
      showToast("Please select an amenity, date and time slot", "error");
      return;
    }
    setSubmitting(true);
    try {
      await apiFetch("/bookings", {
        method: "POST",
        body: JSON.stringify({
          COMMUNITY_ID: communityId,
          UNIT_NBR: unitNbr,
          AMENITY: form.amenity,
          BOOKING_DATE: form.bookingDate,
          SLOT: form.slot,
          PURPOSE: form.purpose,
          BOOKED_BY: userEmail,
          BOOKED_BY_NAME: userName,
        }),
      });
      showToast("Booking request submitted!");
      setForm({ amenity: "", bookingDate: "", slot: "", purpose: "" });
      setShowForm(false);
      loadBookings();
    } catch (err) {
      const msg = err.message || "";
      if (msg.includes("slot_taken")) {
        showToast("This time slot is already booked. Please choose another.", "error");
      } else {
        showToast("Failed to submit booking", "error");
      }
    } finally {
      setSubmitting(false);
    }
  }

  async function handleCancel(bookingId) {
    try {
      await apiFetch("/bookings", {
        method: "PATCH",
        body: JSON.stringify({
          COMMUNITY_ID: communityId,
          BOOKING_ID: bookingId,
          STATUS: "cancelled",
        }),
      });
      showToast("Booking cancelled");
      loadBookings();
    } catch {
      showToast("Failed to cancel booking", "error");
    }
  }

  const filtered = bookings.filter((b) => {
    if (filterStatus === "all") return true;
    return (b.STATUS || "") === filterStatus;
  });

  return (
    <div className="p-4">
      {/* Toasts */}
      <div className="fixed top-4 right-4 z-[100] space-y-2">
        {toasts.map((t) => (
          <div key={t.id} className={`px-4 py-2 rounded-lg shadow-lg text-white text-sm ${t.type === "error" ? "bg-red-500" : "bg-green-500"}`}>
            {t.message}
          </div>
        ))}
      </div>

      {/* Header */}
      <div className="flex items-center justify-between flex-wrap gap-2 mb-4">
        <h2 className="text-lg font-bold">Clubhouse & Amenities</h2>
        <button onClick={loadBookings} className="p-2 rounded-lg bg-gray-100 active:bg-gray-200">
          <RefreshCw size={16} className={loading ? "animate-spin" : ""} />
        </button>
      </div>

      {/* Trial limit banner */}
      {isTrial !== false && trialMaxPerMonth != null && trialMaxPerMonth >= 0 && (
        <div style={{
          background: bookingAtLimit ? '#fef2f2' : '#fffbeb',
          border: `1px solid ${bookingAtLimit ? '#fca5a5' : '#fbbf24'}`,
          borderRadius: 8, padding: '10px 14px', fontSize: 13, marginBottom: 12,
          color: bookingAtLimit ? '#991b1b' : '#92400e'
        }}>
          {bookingAtLimit
            ? `You've reached the ${trialMaxPerMonth}-booking limit for this month on your free trial. Upgrade to book more.`
            : `Trial: ${thisMonthBookingCount} / ${trialMaxPerMonth} bookings used this month`}
        </div>
      )}

      {/* Book button */}
      <button
        onClick={() => !bookingAtLimit && setShowForm(true)}
        disabled={bookingAtLimit}
        className={`w-full py-3 rounded-xl font-medium flex items-center justify-center gap-2 mb-3 ${bookingAtLimit ? 'bg-gray-200 text-gray-400 cursor-not-allowed' : 'bg-blue-600 text-white active:bg-blue-700'}`}
      >
        <Plus size={18} /> Book an Amenity
      </button>

      {/* Booking Modal */}
      {showForm && (
        <Modal title="Book an Amenity" onClose={() => setShowForm(false)}>
          <form onSubmit={handleSubmit} className="space-y-4">
              {/* Amenity Selection */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Select Amenity *</label>
                {amenitiesLoading ? (
                  <p className="text-sm text-gray-400">Loading amenities...</p>
                ) : amenities.length === 0 ? (
                  <p className="text-sm text-gray-400">No amenities configured for this community</p>
                ) : (
                <div className="grid grid-cols-3 gap-2">
                  {amenities.map((a) => {
                    const Icon = getIcon(a.ICON);
                    const selected = form.amenity === a.AMENITY_ID;
                    return (
                      <button
                        key={a.AMENITY_ID}
                        type="button"
                        onClick={() => setForm({ ...form, amenity: a.AMENITY_ID })}
                        className={`flex flex-col items-center gap-1 p-3 rounded-xl text-xs border-2 transition-colors ${
                          selected
                            ? "border-blue-500 bg-blue-50 text-blue-700"
                            : "border-gray-200 text-gray-600"
                        }`}
                      >
                        <Icon size={20} />
                        <span>{a.NAME}</span>
                      </button>
                    );
                  })}
                </div>
                )}
              </div>

              {/* Date */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Date *</label>
                <input
                  type="date"
                  value={form.bookingDate}
                  min={today}
                  onChange={(e) => setForm({ ...form, bookingDate: e.target.value })}
                  className="w-full p-2 border border-gray-300 rounded-lg text-sm"
                  required
                />
              </div>

              {/* Time Slot */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Time Slot *</label>
                <div className="grid grid-cols-2 gap-2">
                  {TIME_SLOTS.map((s) => (
                    <button
                      key={s.value}
                      type="button"
                      onClick={() => setForm({ ...form, slot: s.value })}
                      className={`px-3 py-2 rounded-lg text-xs border-2 transition-colors ${
                        form.slot === s.value
                          ? "border-blue-500 bg-blue-50 text-blue-700 font-medium"
                          : "border-gray-200 text-gray-600"
                      }`}
                    >
                      {s.label}
                    </button>
                  ))}
                </div>
              </div>

              {/* Purpose */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Purpose (optional)</label>
                <input
                  type="text"
                  value={form.purpose}
                  onChange={(e) => setForm({ ...form, purpose: e.target.value })}
                  placeholder="e.g., Birthday party, Yoga session"
                  className="w-full p-2 border border-gray-300 rounded-lg text-sm"
                />
              </div>

              <div className="flex justify-end gap-2 pt-2">
                <button type="button" onClick={() => setShowForm(false)} className="px-4 py-2 text-sm text-gray-600 bg-gray-100 rounded-lg">
                  Cancel
                </button>
                <button type="submit" disabled={submitting} className="px-4 py-2 text-sm text-white bg-blue-600 rounded-lg disabled:opacity-50">
                  {submitting ? "Submitting..." : "Submit Booking"}
                </button>
              </div>
            </form>
        </Modal>
      )}

      {/* Filter pills */}
      <div className="filter-pills mb-4 pb-2">
        {["all", "pending", "approved", "rejected"].map((s) => (
          <button
            key={s}
            onClick={() => setFilterStatus(s)}
            className={`filter-pill px-4 py-2 rounded-lg text-sm ${
              filterStatus === s ? "bg-blue-600 text-white" : "bg-gray-100 text-gray-700"
            }`}
          >
            {s.charAt(0).toUpperCase() + s.slice(1)}
          </button>
        ))}
      </div>

      {/* Booking Cards */}
      <div className="space-y-3">
        {loading && <div className="text-center py-8 text-gray-400">Loading...</div>}
        {!loading && filtered.length === 0 && (
          <div className="text-center py-8 text-gray-400">
            <FaBuilding size={28} className="mx-auto mb-2 text-gray-300" />
            <p className="text-sm text-white">No bookings found</p>
            <p className="text-xs mt-1 text-white">Tap "Book an Amenity" to get started</p>
          </div>
        )}
        {!loading &&
          filtered.map((b) => {
            const amenity = getAmenityInfo(b.AMENITY, amenities);
            const AmenityIcon = amenity.icon;
            const status = b.STATUS || "pending";
            return (
              <div key={b.BOOKING_ID} className="bg-white border border-gray-200 rounded-xl p-4">
                <div className="flex items-start gap-3">
                  <div className={`p-2 rounded-lg flex-shrink-0 ${
                    status === "approved" ? "bg-green-100 text-green-700" :
                    status === "rejected" ? "bg-red-100 text-red-700" :
                    "bg-blue-100 text-blue-700"
                  }`}>
                    <AmenityIcon size={20} />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center justify-between gap-2">
                      <h3 className="font-medium text-gray-900 truncate">{amenity.label}</h3>
                      <span className={`flex-shrink-0 px-2 py-0.5 rounded text-xs font-medium ${STATUS_BADGES[status] || "bg-gray-100 text-gray-600"}`}>
                        {status.charAt(0).toUpperCase() + status.slice(1)}
                      </span>
                    </div>
                    <div className="flex items-center flex-wrap gap-x-3 gap-y-1 mt-1 text-sm text-gray-500">
                      <span className="flex items-center gap-1">
                        <Calendar size={12} /> {formatDate(b.BOOKING_DATE)}
                      </span>
                      <span className="flex items-center gap-1">
                        <Clock size={12} /> {getSlotLabel(b.SLOT)}
                      </span>
                    </div>
                    {b.PURPOSE && <p className="text-xs text-gray-500 mt-1">📝 {b.PURPOSE}</p>}
                    {b.REJECT_REASON && (
                      <p className="text-xs text-red-500 mt-1">Reason: {b.REJECT_REASON}</p>
                    )}
                    {b.REVIEWED_BY_NAME && (
                      <p className="text-xs text-gray-400 mt-1">
                        {status === "approved" ? "Approved" : "Reviewed"} by {b.REVIEWED_BY_NAME}
                      </p>
                    )}
                    {/* Cancel only if pending */}
                    {status === "pending" && (
                      <button
                        onClick={() => handleCancel(b.BOOKING_ID)}
                        className="mt-2 flex items-center gap-1 px-3 py-1.5 bg-red-50 text-red-600 rounded-lg text-xs font-medium active:bg-red-100"
                      >
                        <XCircle size={14} /> Cancel
                      </button>
                    )}
                  </div>
                </div>
              </div>
            );
          })}
      </div>
    </div>
  );
}
